import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createStandardOrder, getOrderSeatCountForShowtime, getTicketOptionsForShowtime, hasOrderBeenCreated, isEmptyArray, isNonEmptyArray, isSeatFirstOrderingForShowtime, LoadingState, loadTicketPricesForShowtime, NotificationType, removeNotification, saveOrderTicketsForShowtime, setOrderTicketQuantityForShowtime, validateOrderTicketsForShowtime } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useLoadAction } from '@vista-digital/react-tools';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useState } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useShowtimeContext } from '../../../context/showtime/hooks/use-showtime-context';
import { CounterStyle } from '../../../subcomponents/counter/counter';
import { Notification } from '../../../subcomponents/notification/notification';
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { mapTicketPickerErrorMessage } from './mappers/ticket-picker-error-message-mapper';
import { TicketPickerCounter } from './subcomponents/counter';
import { TicketPickerMessage, TicketPickerMessageType } from './subcomponents/message';
import { TicketPickerTableBody } from './subcomponents/table-body';
import { mapTicketPickerContext, TicketPickerContextInstance } from './types/context';
import { defaultTicketPickerProps, ticketPickerPropsValidator } from './types/props';
import { addProceedErrorNotification, removeProceedErrorNotification } from './utils/notification';
const b = bemClassBuilder('v-ticket-picker');
const invalidSelectionNotificationId = getIdForComponentNotification('ticket-picker', NotificationType.Error, 'invalid-selection');
export const TicketPickerBase = (props, ref) => {
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { config, displayText, assets, subcomponents } = props;
    const { showtime, showtimeId } = useShowtimeContext();
    const [validationErrors, setValidationErrors] = useState([]);
    const componentId = useId();
    const { Header, Skeleton } = subcomponents;
    const loadAction = useCallback(() => loadTicketPricesForShowtime(showtimeId), [showtimeId]);
    const { loadingState } = useLoadAction(loadAction);
    const handleTicketQuantityChanged = useCallback((ticket, newQuantity) => {
        setOrderTicketQuantityForShowtime(showtimeId, ticket, newQuantity);
        setValidationErrors([]);
    }, [showtimeId]);
    const validateTicketSelections = useCallback(() => {
        const validationResult = validateOrderTicketsForShowtime(showtimeId);
        setValidationErrors(validationResult.isValid ? [] : validationResult.errors);
        return validationResult;
    }, [showtimeId]);
    const handleProceed = useCallback(async () => {
        try {
            removeProceedErrorNotification();
            if (!showtime)
                throw new Error('Cannot handle proceed without showtime');
            const validationResult = validateTicketSelections();
            if (validationResult.isValid) {
                if (!hasOrderBeenCreated())
                    await createStandardOrder(showtime.siteId);
                await saveOrderTicketsForShowtime(showtimeId);
                return true;
            }
            else {
                return false;
            }
        }
        catch (error) {
            addProceedErrorNotification(displayText, error);
            return false;
        }
    }, [validateTicketSelections, displayText, showtimeId, showtime]);
    useImperativeHandle(ref, () => ({
        validateTicketSelections,
        handleProceed
    }));
    useEffect(() => {
        if (config.displayErrorsInline)
            return;
        if (isEmptyArray(validationErrors))
            removeNotification(invalidSelectionNotificationId);
    }, [config.displayErrorsInline, validationErrors]);
    const notificationMessages = useMemo(() => validationErrors.map(error => mapTicketPickerErrorMessage(error, displayText)), [displayText, validationErrors]);
    const handleNotificationCloseButtonClicked = useCallback(() => {
        setValidationErrors([]);
    }, []);
    if (loadingState === LoadingState.Failure) {
        return (_jsx(TicketPickerMessage, { messageType: TicketPickerMessageType.FailedToLoadData, displayText: displayText, assets: assets }));
    }
    const tickets = getTicketOptionsForShowtime(showtimeId);
    if (!showtime || !tickets) {
        return enableSkeletonLoadingIndicators ? _jsx(Skeleton, {}) : null;
    }
    if (isEmptyArray(tickets))
        throw new Error('Cannot render the Ticket Picker with no ticket options.');
    const isSeatFirstOrdering = isSeatFirstOrderingForShowtime(showtimeId);
    if (isSeatFirstOrdering && getOrderSeatCountForShowtime(showtimeId) === 0) {
        throw new Error('Cannot render the Ticket Picker in seat-first ordering mode when no seats have been selected.');
    }
    const isInvalidSelection = isNonEmptyArray(validationErrors);
    const counterStyle = isInvalidSelection ? CounterStyle.Warning : CounterStyle.Info;
    return (_jsx(TicketPickerContextInstance.Provider, Object.assign({ value: mapTicketPickerContext(props, componentId) }, { children: _jsxs("div", Object.assign({ className: b.block() }, { children: [isSeatFirstOrdering && _jsx(TicketPickerCounter, { style: counterStyle }), _jsx(Header, {}), _jsx(TicketPickerTableBody, { tickets: tickets, onTicketQuantityChanged: handleTicketQuantityChanged }), _jsx(AnimatePresence, { children: isInvalidSelection && (_jsx(Notification, { id: invalidSelectionNotificationId, messages: notificationMessages, type: NotificationType.Warning, displayInline: config.displayErrorsInline, onCloseButtonClick: handleNotificationCloseButtonClicked, wrapperClassName: "v-ticket-picker-notification" })) })] })) })));
};
export const TicketPicker = observer(forwardRef(TicketPickerBase));
TicketPicker.displayName = 'TicketPicker';
/**
 * The **Ticket Picker** component renders the tickets available for a selected showtime.
 */
export const TicketPickerComponent = withComponentWrapper(TicketPicker, defaultTicketPickerProps, ticketPickerPropsValidator);
